import React from 'react';
import { TarotCardData } from '../types/tarot';
import { useFrameAuth } from '../hooks/useFrameAuth';
import { shareToWarpcast, shareToTwitter } from '../utils/farcasterShare';
import ShareArrowIcon from './ShareArrowIcon';
import { ModalCloseButton } from './ModalCloseButton';

interface ShareModalProps {
  selectedCards: TarotCardData[];
  onClose: () => void;
  mode?: 'reading' | 'insight';
  insightText?: string;
  insightLabel?: string;
  shareUrlOverride?: string;
}

// Custom SVG Icons
const FarcasterIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg width="20" height="20" viewBox="0 0 1000 1000" fill="currentColor" className={className}>
    <path d="M257.778 155.556H742.222V844.445H671.111V528.889H670.414C662.554 441.677 589.258 373.333 500 373.333C410.742 373.333 337.446 441.677 329.586 528.889H328.889V844.445H257.778V155.556Z"/>
    <path d="M128.889 253.333L157.778 351.111H182.222V746.667C169.949 746.667 160 756.616 160 768.889V795.556H155.556C143.283 795.556 133.333 805.505 133.333 817.778V844.445H382.222V817.778C382.222 805.505 372.273 795.556 360 795.556H355.556V768.889C355.556 756.616 345.606 746.667 333.333 746.667H306.667V253.333H128.889Z"/>
    <path d="M675.556 746.667C663.283 746.667 653.333 756.616 653.333 768.889V795.556H648.889C636.616 795.556 626.667 805.505 626.667 817.778V844.445H875.556V817.778C875.556 805.505 865.606 795.556 853.333 795.556H848.889V768.889C848.889 756.616 838.94 746.667 826.667 746.667V351.111H851.111L880 253.333H702.222V746.667H675.556Z"/>
  </svg>
);

const XIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M14.095479,10.316482L22.286354,1h-1.940718l-7.115352,8.087682L7.551414,1H1l8.589488,12.231093L1,23h1.940717l7.509372-8.542861L16.448587,23H23L14.095479,10.316482z M11.436522,13.338465l-0.871624-1.218704l-6.924311-9.68815h2.981339l5.58978,7.82155l0.867949,1.218704l7.26506,10.166271h-2.981339L11.436522,13.338465z"/>
  </svg>
);

export const ShareModal: React.FC<ShareModalProps> = ({
  selectedCards,
  onClose,
  mode = 'reading',
  insightText,
  insightLabel = 'Cosmic Insight',
  shareUrlOverride,
}) => {
  const { user, isAuthenticated, isLoading } = useFrameAuth();

  // Debug logging for user authentication
  React.useEffect(() => {
    console.log('ShareModal: User auth state:', { user, isAuthenticated, isLoading });
  }, [user, isAuthenticated, isLoading]);

  // Share to Farcaster
  const shareText = React.useMemo(() => {
    if (mode === 'insight' && insightText) {
      return `✨ ${insightLabel}: ${insightText}\n\n🔮 Ask fate inside Stradamus and pull your own spread today.`;
    }
    return undefined;
  }, [mode, insightText, insightLabel]);

  const handleFarcasterShare = () => {
    const userFid = user?.fid?.toString();
    shareToWarpcast(selectedCards, userFid, {
      textOverride: shareText,
      shareUrlOverride,
    });
  };

  // Share to Twitter
  const handleTwitterShare = () => {
    const userFid = user?.fid?.toString();
    shareToTwitter(selectedCards, userFid, {
      textOverride: shareText,
      shareUrlOverride,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[80] p-4">
      <div 
        className="absolute inset-0" 
        onClick={onClose}
      />
      
      <div className="relative bg-gradient-to-b from-slate-800 to-purple-900 rounded-2xl max-w-lg w-full mx-4 min-h-[400px] max-h-[90vh] overflow-y-auto p-6 shadow-2xl border border-purple-400/30">
        {/* Close Button - Fixed alignment */}
        <ModalCloseButton onClose={onClose} className="text-white/80" />

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-3">
            <ShareArrowIcon className="w-6 h-6 text-white" />
          </div>
          <h2 className="crypto-heading text-xl text-white mb-2">
            {mode === 'insight' ? 'Share Your Insight' : 'Share Your Reading'}
          </h2>
          <p className="text-white/70 text-sm font-medium tracking-wide">
            {mode === 'insight'
              ? 'Spread the magic with your friends and invite them to try Stradamus.'
              : 'Share your mystical crypto insights with the world'}
          </p>
          {mode === 'reading' && selectedCards.length > 0 && (
            <p className="text-white/50 text-xs mt-2 uppercase tracking-[0.2em]">
              Deck: {selectedCards[0].deckKey === 'standard' ? 'Standard Tarot' : 'Crypto Tarot'}
            </p>
          )}
        </div>

        {/* Frame User Info */}
        {isAuthenticated && user && (
          <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-green-400 text-sm font-medium">
                Connected as @{user.username}
              </span>
            </div>
          </div>
        )}

        {/* Share Options */}
        <div className="space-y-3">
          {/* Farcaster Share */}
          <button
            onClick={handleFarcasterShare}
            className="w-full flex items-center justify-between p-4 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 rounded-lg transition-colors group"
          >
            <div className="flex items-center space-x-3">
              <FarcasterIcon className="w-5 h-5 text-purple-400" />
              <span className="text-white font-medium">Open in Farcaster</span>
            </div>
          </button>

          {/* X/Twitter Share */}
          <button
            onClick={handleTwitterShare}
            className="w-full flex items-center justify-between p-4 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-lg transition-colors group"
          >
            <div className="flex items-center space-x-3">
              <XIcon className="w-5 h-5 text-blue-400" />
              <span className="text-white font-medium">Share on X</span>
            </div>
          </button>
        </div>

        {/* Reading Preview */}
        <div className="mt-6 p-4 bg-black/20 rounded-lg border border-white/10">
          <h3 className="text-white font-medium mb-2">
            {mode === 'insight' ? `${insightLabel}:` : 'Your Reading:'}
          </h3>
          {mode === 'insight' && insightText ? (
            <p className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap">
              {insightText}
            </p>
          ) : (
            <div className="space-y-2">
              {selectedCards.map((card, index) => (
                <div key={card.id} className="text-sm">
                  <span className="text-purple-400 font-medium">{index + 1}. {card.name}</span>
                  <span className="text-white/70 ml-2">{card.meaning}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
